import {
  Arg,
  COMPONENT_STATE_ARGS,
  FLEX_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { CENTER, SPACE_BETWEEN } from '../../../../utils/spds/constants/css-layout';
import {
  TOAST_DEFAULT_TIMEOUT,
  TOAST_ICONS,
  TOAST_SUPPORTED_TYPES,
} from '../../../../utils/spds/constants/feedback';
import { fn } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import CloseButton from '../../action/button/close.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import GhostButton from '../../action/button/ghost.gjs';
import Super from '../../core/base.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: `
Individual toast notification message rendered as a \`popover="manual"\` element so each toast lives in the browser top layer above modals and other layered UI.

The toast data object is passed as the \`@toast\` argument and contains type, message, and optional cta, alignItems, flex, and gap properties. Stacking position is controlled by \`@offset\` (any CSS length); entry/exit animations are gated by \`@animated\`.

The \`Spds::Feedback::Toast::Container\` component must be present at the root of the application to handle toast service events; it manages registration, ordering, and offset computation for each rendered Message.
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, [{ name: 'animated', defaultValue: true, private: true }]),
    pick(FLEX_ARGS, [
      { name: 'alignItems', default: CENTER, private: true },
      { name: 'flex', default: '1 0 auto', private: true },
      { name: 'gap', default: '16px', private: true },
      { name: 'justifyContent', default: SPACE_BETWEEN, private: true },
    ]),
  ],
  meta: {
    index: Arg.number({
      description: 'Position index of the toast in the container',
      private: true,
    }),
    offset: Arg.string({
      default: '16px',
      description:
        'Distance from the bottom of the viewport to the bottom of this toast (any CSS length unit, e.g. `16px`, `1rem`, `2vh`).',
      private: true,
    }),
    onManualDismiss: Arg.action({
      description: 'Action triggered when the toast is manually dismissed',
    }),
    onReady: Arg.action({
      description:
        'Internal callback fired from `setup` once the popover has been opened so the container can recompute stacking offsets against a known DOM size.',
      private: true,
    }),
    toast: Arg.object({
      description: 'Toast data object containing type, message, and optional cta properties',
    })
      .required()
      .properties({
        cta: Arg.object({ description: 'Optional CTA button configuration' }).properties({
          action: Arg.action({
            description: 'Action to perform when the CTA button is clicked',
          }).required(),
          text: Arg.string({ description: 'CTA button text', initialValue: 'Undo' }).required(),
        }),
        message: Arg.string({
          description: 'Toast message text',
          initialValue: 'An example toast',
        }).required(),
        timeout: Arg.number({
          default: TOAST_DEFAULT_TIMEOUT,
          description: 'Toast timeout in milliseconds',
        }),
        type: Arg.oneOf(TOAST_SUPPORTED_TYPES, {
          description: 'Visual style variant of the toast',
        }).required(),
      }),
  },
  name: 'Toast Message',
  resources: {
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/128371-toast',
  },
});

export default class SpdsFeedbackToastMessage extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-toast-message';
  classesToMapToAttrs = ['animated', 'hasCta', 'type'];
  // style attr doesn't merge, so need to repeat some props here
  propsToMapToAttrs = ['alignItems', 'flex', 'gap', 'justifyContent', 'offset'];
  styles = styles;

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get animated() {
    return this.argOrDefault('animated');
  }

  get flex() {
    return this.argOrDefault('flex');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get hasCta() {
    return !isEmpty(this.toast.cta);
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  get offset() {
    return this.argOrDefault('offset');
  }

  get toast() {
    return this.args.toast;
  }

  get type() {
    return this.toast.type;
  }

  get icon() {
    return TOAST_ICONS[this.type];
  }

  ctaClick = async () => {
    let shouldClose = await Promise.resolve(this.toast.cta.action?.());

    if (shouldClose || isEmpty(shouldClose)) {
      this.args.onManualDismiss?.(this.toast);
    }
  };

  /**
   * Opens the popover synchronously when the `setupComponent` modifier
   * installs (the element is in the DOM at that point) and notifies the
   * container so it can recompute stacking offsets with this message's
   * known dimensions. The `isConnected` guard and try/catch guard against
   * rapid teardown scenarios where the element is removed mid-render and
   * `showPopover` would otherwise throw `Failed to execute 'showPopover'
   * on 'HTMLElement': Invalid on disconnected popover elements.`
   */
  setup = () => {
    if (!this.baseElement?.isConnected) return;

    try {
      this.baseElement.showPopover?.();
    } catch {
      // Element became disconnected between modifier install and showPopover;
      // the component is being torn down and cleanup will fire.
    }

    this.args.onReady?.(this);
  };

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @flex={{this.flex}}
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      aria-live="assertive"
      class={{this.classes}}
      data-test-spds-feedback-toast-message={{or @index "0"}}
      popover="manual"
      role="status"
      style={{this.customProperties}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.icon}}
        {{svgJar this.icon class="icon" data-test-spds-feedback-toast-message-icon=""}}
      {{/if}}
      <FlexRow @flex="1 0 auto" class="text" data-test-spds-feedback-toast-message-text>
        {{@toast.message}}
      </FlexRow>
      {{#if @toast.cta}}
        <GhostButton
          class="cta-btn"
          data-test-spds-feedback-toast-message-cta-button
          type="button"
          {{on "click" this.ctaClick}}
        >
          {{@toast.cta.text}}
        </GhostButton>
      {{/if}}
      <CloseButton
        @close={{fn @onManualDismiss @toast}}
        class="dismissal-btn"
        data-test-spds-feedback-toast-message-close-button
      />
    </FlexRow>
  </template>
}

export const TOAST_MESSAGE_METADATA = SpdsFeedbackToastMessage.metadata;
